<script setup lang="ts">
import { computed } from "vue";
import { useTheme } from "@/composables/useTheme";
import PrintHQPrinterScene from "@/components/auth/PrintHQPrinterScene.vue";

// The scene follows the app's own theme setting (Dark, Light or System),
// not just the operating system.
const { isDark } = useTheme();
const sceneTheme = computed(() => (isDark.value ? "dark" : "light"));
</script>

<template>
	<div class="min-h-screen bg-bg text-fg lg:grid lg:grid-cols-[56%_44%]">
		<!-- Left: the printer scene. Stacks above the form below 1024px. -->
		<section class="auth-hero min-w-0 lg:sticky lg:top-0 lg:self-start" aria-label="PrintHQ">
			<PrintHQPrinterScene :theme="sceneTheme">
				<img
					src="/logo-wordmark.svg"
					alt="PrintHQ"
					class="absolute left-6 top-5 w-[96px] h-auto sm:w-[112px] lg:left-9 lg:top-8 lg:w-[136px]"
				/>
				<div class="absolute left-6 right-16 bottom-6 lg:left-9 lg:right-40 lg:bottom-14">
					<p class="text-[11px] leading-4 font-semibold uppercase tracking-[0.12em] text-accent-text">
						Less managing. More making.
					</p>
					<h1 class="mt-2.5 mb-3 text-[28px] lg:text-[36px] leading-[1.13] tracking-[-0.04em] font-semibold text-fg">
						Your next great print<br />starts here.
					</h1>
					<p class="hidden sm:block text-sm leading-relaxed text-fg-2">
						Your files, filament and printers.<br />One place to keep creating.
					</p>
				</div>
			</PrintHQPrinterScene>
		</section>

		<!-- Right: the form -->
		<main class="flex flex-col min-w-0 px-4 pt-10 pb-6 sm:px-8 lg:min-h-dvh lg:px-10 lg:pt-12">
			<div class="w-full max-w-sm m-auto">
				<slot />
			</div>
			<footer class="mt-10 flex justify-between gap-4 text-[11px] text-fg-3">
				<span>Built for the things you make.</span>
				<span>© {{ new Date().getFullYear() }} PrintHQ</span>
			</footer>
		</main>
	</div>
</template>

<style scoped>
/* Scene height: a 410px band when stacked; the full window on desktop,
   never shorter than the kit's designed 660px so the printer and
   headline both fit. */
.auth-hero {
	--printhq-scene-height: 410px;
	--printhq-scene-mobile-height: 410px;
}
@media (min-width: 1024px) {
	.auth-hero {
		--printhq-scene-height: max(660px, 100dvh);
	}
}
</style>
